import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCalendarDays, faClipboardList, faClockRotateLeft, faPlus, faTrash, faDownload } from "@fortawesome/free-solid-svg-icons";
import { useParams, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Dashboard from "../dashboard/Dashboard";
import LoadingOverlay from "../common/LoadingOverlay/LoadingOverlay";
import './ProjectDetail.css'
import { getAuthToken } from '../../utils/authStorage';
import { useUser } from "../../context/UserContext";

import ProjectSharing from "./ProjectSharing";

const ProjectDetails = () => {
  const [showSharing,setShowSharing] = useState(false);
  const location = useLocation();
  // const { projectName, projectDescription, creationDate } = location.state || {};
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newInspectionName, setNewInspectionName] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [inspectedBy, setInspectedBy] = useState("");
  const [inspectionDomain, setInspectionDomain] = useState("ELECTRIC_NETWORK");
  const [imageModality, setImageModality] = useState("RGB");
  const [kmzFile, setKmzFile] = useState(null);
  const [hasKmz, setHasKmz] = useState(false);
  const [kmzURL, setKmzUrl] = useState(null);
  const [loadingInspections, setLoadingInspections] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportInspectionIds, setSelectedExportInspectionIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const { isViewer } = useUser();

  const navigate = useNavigate();

  useEffect(() => {
    if (!projectId) return;

    const token = getAuthToken();
    const headers = { Authorization: `Bearer ${token}` };

    const fetchProject = axios.get(`/api/projects/${projectId}`, { headers })
      .then(res => setProject(res.data))
      .catch(err => console.error("Error fetching project:", err));

    const fetchInspections = axios.get(`/api/inspections/by-project/${projectId}/cards`, { headers })
      .then(res => setInspections(res.data))
      .catch(err => console.error("Error fetching inspections:", err))
      .finally(() => setLoadingInspections(false));

    // Ambas requests corren en paralelo
    Promise.all([fetchProject, fetchInspections]);

  }, [projectId]);

  const handleNewInspection = () => {
    if (isViewer) return;
    setShowModal(true);
  };

  const completedInspections = inspections.filter((insp) => Number(insp?.analysedCount ?? 0) > 0);

  const openExportModal = () => {
    setExportError('');
    setSelectedExportInspectionIds(completedInspections.map((insp) => insp.id));
    setShowExportModal(true);
  };

  const toggleExportInspection = (inspectionId) => {
    setSelectedExportInspectionIds((current) =>
      current.includes(inspectionId)
        ? current.filter((id) => id !== inspectionId)
        : [...current, inspectionId],
    );
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportProject = async (type) => {
    if (selectedExportInspectionIds.length === 0) {
      setExportError('Select at least one inspection with results.');
      return;
    }

    setIsExporting(true);
    setExportError('');
    try {
      const token = getAuthToken();
      const endpoint = type === 'csv'
        ? `/api/projects/${projectId}/exports/findings.csv`
        : `/api/projects/${projectId}/exports/summary.pdf`;
      const response = await axios.post(
        endpoint,
        { inspectionIds: selectedExportInspectionIds },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
        },
      );
      const safeProjectName = (project?.name || 'project').replace(/[^a-z0-9_-]+/gi, '_');
      downloadBlob(
        response.data,
        type === 'csv'
          ? `${safeProjectName}_findings.csv`
          : `${safeProjectName}_summary.pdf`,
      );
    } catch (err) {
      console.error('Error exporting project:', err);
      setExportError('Could not generate the export. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const fmtDate = (d) => {
    if (!d) return "No date";
    const t = new Date(d);
    return isNaN(t)
      ? d
      : t.toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
  };

  const token = getAuthToken();

  const inspectionDomainLabels = {
    ELECTRIC_NETWORK: "Electric Network",
    EMISSIONS: "Emissions",
    THERMAL_PIPELINE: "Thermal Pipeline",
    OIL_SPILL: "Oil Spill",
    PPE: "PPE",
    SOLAR_PANEL: "Solar Panels",
  };

  const imageModalityLabels = {
    RGB: "RGB",
    THERMAL: "Thermal",
    OGI: "OGI",
  };

  const modalityOptionsByDomain = {
    ELECTRIC_NETWORK: ["RGB", "THERMAL"],
    EMISSIONS: ["RGB", "THERMAL", "OGI"],
    THERMAL_PIPELINE: ["THERMAL"],
    OIL_SPILL: ["RGB"],
    PPE: ["RGB"],
    SOLAR_PANEL: ["RGB", "THERMAL"],
  };

  const handleInspectionDomainChange = (value) => {
    setInspectionDomain(value);
    const options = modalityOptionsByDomain[value] || ["RGB"];
    if (!options.includes(imageModality)) {
      setImageModality(options[0]);
    }
  };

 function openInspection(insp) {
  const imagesCount = Number(insp?.imagesCount ?? 0);
  const analysedCount = Number(insp?.analysedCount ?? 0);
  const hasResults = Boolean(insp?.hasResults) || analysedCount > 0;

  const baseState = {
    inspectionId: insp.id,
    inspectionName: insp.name,
    projectId,
    projectName: project?.name,
    createdBy: insp.createdBy,
    createdAt: insp.created_at,
    kmzFile: insp.kmz_Url,
    inspectionDomain: insp.inspectionDomain,
    imageModality: insp.imageModality,
  };

  if (hasResults) {
    navigate('/results', {
      state: {
        ...baseState,
        serverMode: true,
      },
    });
    return;
  }

  if (isViewer) return;

  if (insp.imageModality === "THERMAL" && imagesCount > 0) {
    navigate(`/inspections/${insp.id}/results`, {
      state: {
        ...baseState,
        serverMode: true,
      },
    });
    return;
  }

  if (imagesCount > 0) {
    navigate(`/inspections/${insp.id}/selectImages`, { state: baseState });
    return;
  }

  navigate(`/inspections/${insp.id}/upload`, { state: baseState });
}


  const confirmNewInspection = async () => {
    if (!newInspectionName.trim()) return;

    setIsCreating(true);
    try {
      const token = getAuthToken();
      const formData = new FormData();

      formData.append("name", newInspectionName);
      formData.append("projectId", projectId);
      formData.append("date", inspectionDate);
      formData.append("inspectedBy", inspectedBy);
      formData.append("inspectionDomain", inspectionDomain);
      formData.append("imageModality", imageModality);
      if (kmzFile) {
        formData.append("kmzFile", kmzFile);
      }
      const res = await axios.post("/api/inspections", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      const newInspection = res.data;
      setKmzUrl(newInspection.kmz_Url);
      setShowModal(false);
      setNewInspectionName("");
      setInspectionDomain("ELECTRIC_NETWORK");
      setImageModality("RGB");

      navigate(`/inspections/${newInspection.id}/upload`, {
        state: {
          inspectionId: newInspection.id,
          inspectionName: newInspection.name,
          projectId,
          projectName: project.name,
          createdBy: inspectedBy,
          createdAt: inspectionDate,
          kmzFile: newInspection.kmz_Url,
          inspectionDomain: newInspection.inspectionDomain,
          imageModality: newInspection.imageModality
        }
      });
    } catch (err) {
      console.error("Error creating inspection:", err);
    } finally {
      setIsCreating(false);
    }
  };

  const confirmDeleteInspection = async () => {
    if (!inspectionToDelete) return;

    setIsDeleting(true);
    setDeleteError('');
    try {
      const token = getAuthToken();
      await axios.delete(`/api/inspections/${inspectionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInspections((prev) => prev.filter((insp) => insp.id !== inspectionToDelete.id));
      setInspectionToDelete(null);
    } catch (err) {
      console.error("Error deleting inspection:", err);
      setDeleteError(err.response?.data?.error || err.response?.data?.message || "Could not delete the inspection.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <LoadingOverlay show={loadingInspections} text="Loading inspections…" />
      <div className="pd-container">
        {/* Header */}
        <div className="pd-header">
          <div className="pd-header-left">
            <h1 className="pd-title">{project?.name || "Project"}</h1>
            {project?.description && (
              <p className="pd-desc">{project.description}</p>
            )}
            {project?.creationDate && (
              <p className="pd-meta">
                Created&nbsp;{fmtDate(project.creationDate)}
              </p>
            )}
          </div>
          <div className="pd-header-actions">
            {!isViewer && <button className="btn btn-outline" onClick={() => setShowSharing(true)}>Share project</button>}
            {showSharing && !isViewer && <ProjectSharing projectId={projectId} onClose={() => setShowSharing(false)} />}
            <button
              className="btn btn-outline"
              type="button"
              onClick={openExportModal}
              disabled={completedInspections.length === 0}
            >
              <FontAwesomeIcon icon={faDownload} />
              Export project
            </button>
            <button
              className="btn btn-outline"
              type="button"
              onClick={() => navigate(`/projects/${projectId}/history`, {
                state: { projectName: project?.name },
              })}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
              Historical comparison
            </button>
            {!isViewer && (
              <button className="pd-add-btn" onClick={handleNewInspection}>
                + Add New Inspection
              </button>
            )}
          </div>
        </div>

        {/* Grid de Inspecciones */}
        <div className="pd-grid">
          {!loadingInspections && inspections.length === 0 && (
            <div className="pd-empty">
              <div className="pd-empty-icon">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <h3 className="pd-empty-title">No inspections yet</h3>
              <p className="pd-empty-sub">
                {isViewer
                  ? "No inspections with completed results are available for this viewer profile yet."
                  : "Add the first inspection to this project to start uploading and analyzing images."}
              </p>
              {!isViewer && (
                <button className="pd-empty-btn" onClick={handleNewInspection}>
                  <FontAwesomeIcon icon={faPlus} /> Add Inspection
                </button>
              )}
            </div>
          )}
          {inspections.map((insp) => {
              const imagesCount = insp.imagesCount ?? 0;
              const analysedCount = insp.analysedCount ?? 0;
              const isThermal = insp.imageModality === "THERMAL";
              const progress = isThermal && imagesCount > 0
                ? 100
                : imagesCount > 0
                  ? Math.round((analysedCount / imagesCount) * 100)
                  : 0;
              const status = isThermal && imagesCount > 0
                ? "Ready"
                : insp.hasResults
                  ? "Completed"
                  : "In Progress";
              const domainLabel = inspectionDomainLabels[insp.inspectionDomain] || insp.inspectionDomain || "Electric Network";
              const modalityLabel = imageModalityLabels[insp.imageModality] || insp.imageModality || "RGB";

              return (
                <article key={insp.id} className="insp-card">
                  <header className="insp-head">
                    <h3 className="insp-title">{insp.name}</h3>
                    <div className="insp-meta-row">
                      <span className="insp-meta">{fmtDate(insp.created_at)}</span>
                      <span className="insp-dot">•</span>
                      <span className="insp-meta">{imagesCount} images</span>
                      <span className="insp-dot">•</span>
                      <span className="insp-meta">{domainLabel} · {modalityLabel}</span>
                    </div>
                    <span
                      className={`insp-badge ${status === "Completed" ? "is-completed" : "is-progress"}`}
                    >
                      {status}
                    </span>
                  </header>

                  <div className="insp-progress">
                    <div className="bar">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${Math.max(0, Math.min(100, progress))}%`,
                        }}
                      />
                    </div>
                    <span className="pct">{progress}%</span>
                  </div>

                  <footer className="insp-actions">
                    <button
                      className="btn btn-light"
                      onClick={() => {
                        const baseState = {
                          inspectionId: insp.id,
                          inspectionName: insp.name,
                          projectId,
                          projectName: project?.name,
                          createdBy: insp.createdBy,
                          createdAt: insp.created_at,
                          inspectionDomain: insp.inspectionDomain,
                          imageModality: insp.imageModality,
                        };
                        if ((insp.imagesCount ?? 0) === 0 && !isViewer) {
                          // Sin imágenes → pantalla de upload
                          navigate(`/inspections/${insp.id}/upload`, { state: baseState });
                        } else if (insp.imageModality === "THERMAL" || (insp.analysedCount ?? 0) >= (insp.imagesCount ?? 1)) {
                          // Todas analizadas → pantalla de resultados
                          navigate('/results', {
                            state: {
                              ...baseState,
                              serverMode: true,
                            }
                          });
                        } else if (!isViewer) {
                          // Tiene imágenes (parcial o sin analizar) → SelectImages
                          navigate(`/inspections/${insp.id}/selectImages`, {
                            state: {
                              ...baseState,
                              imagesCount: insp.imagesCount,
                              analysedCount: insp.analysedCount,
                            }
                          });
                        }
                      }}
                    >
                      👁️ View Details
                    </button>
                    {!isViewer && (
                      <>
                        <button
                          className="btn btn-outline"
                          onClick={() => navigate(`/inspections/${insp.id}/edit`)}
                        >
                          ✎ Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => {
                            setDeleteError('');
                            setInspectionToDelete(insp);
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      </>
                    )}
                  </footer>
                </article>
              );
          })}
        </div>

        {showExportModal && (
          <div className="confirm-overlay" role="dialog" aria-modal="true">
            <div className="confirm-modal project-export-modal">
              <header className="confirm-header">
                <h2>Export Project</h2>
                <button
                  className="confirm-close"
                  onClick={() => !isExporting && setShowExportModal(false)}
                  aria-label="Close"
                  disabled={isExporting}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>

              <p className="confirm-text">
                Select the inspections to include in the project exports.
              </p>

              <div className="project-export-list">
                {completedInspections.map((insp) => {
                  const checked = selectedExportInspectionIds.includes(insp.id);
                  return (
                    <label className="project-export-row" key={insp.id}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleExportInspection(insp.id)}
                        disabled={isExporting}
                      />
                      <span>
                        <strong>{insp.name}</strong>
                        <small>
                          {insp.analysedCount ?? 0}/{insp.imagesCount ?? 0} analyzed
                          {insp.kmz_Url ? ' · KMZ' : ''}
                        </small>
                      </span>
                    </label>
                  );
                })}
              </div>

              {exportError && <p className="confirm-error">{exportError}</p>}

              <footer className="confirm-actions project-export-actions">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => setSelectedExportInspectionIds(completedInspections.map((insp) => insp.id))}
                  disabled={isExporting}
                >
                  Select all
                </button>
                <button
                  className="btn btn-light"
                  type="button"
                  onClick={() => exportProject('csv')}
                  disabled={isExporting || selectedExportInspectionIds.length === 0}
                >
                  CSV findings
                </button>
                <button
                  className="btn btn-light"
                  type="button"
                  onClick={() => exportProject('pdf')}
                  disabled={isExporting || selectedExportInspectionIds.length === 0}
                >
                  PDF summary
                </button>
              </footer>
            </div>
          </div>
        )}

        {inspectionToDelete && (
          <div className="confirm-overlay" role="dialog" aria-modal="true">
            <div className="confirm-modal">
              <header className="confirm-header">
                <h2>Delete Inspection</h2>
                <button
                  className="confirm-close"
                  onClick={() => !isDeleting && setInspectionToDelete(null)}
                  aria-label="Close"
                  disabled={isDeleting}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>
              <p className="confirm-text">
                Are you sure you want to delete “{inspectionToDelete.name}”? This will remove the inspection and its images from the platform.
              </p>
              {deleteError && <p className="confirm-error">{deleteError}</p>}
              <footer className="confirm-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setInspectionToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger-solid"
                  onClick={confirmDeleteInspection}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* Modal (igual que el tuyo)… */}
        {showModal && (
          <div className="sheet-overlay" role="dialog" aria-modal="true">
            <aside className="sheet" aria-label="Create New Inspection">
              {/* Header */}
              <header className="sheet-header">
                <h2 className="sheet-title">Create New Inspection</h2>
                <button
                  className="sheet-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                  title="Close"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>

              {/* Body */}
              <div className="sheet-body">
                {/* Name */}
                <div className="form-group">
                  <label className="label" htmlFor="insp-name">
                    Inspection Name
                  </label>
                  <input
                    id="insp-name"
                    type="text"
                    value={newInspectionName}
                    onChange={(e) => setNewInspectionName(e.target.value)}
                    placeholder="Enter Inspection name"
                    className="input"
                  />
                </div>

                {/* Date */}
                <div className="form-group">
                  <label className="label" htmlFor="insp-date">
                    Inspection Date
                  </label>
                  <div className="input with-icon">
                    <input
                      id="insp-date"
                      type="date"
                      value={inspectionDate}
                      onChange={(e) => setInspectionDate(e.target.value)}
                      className="control"
                    />
                    <FontAwesomeIcon
                      className="input-icon"
                      icon={faCalendarDays}
                    />
                  </div>
                </div>

                {/* Inspected By */}
                <div className="form-group">
                  <label className="label" htmlFor="insp-by">
                    Inspected By
                  </label>
                  <input
                    id="insp-by"
                    type="text"
                    value={inspectedBy}
                    onChange={(e) => setInspectedBy(e.target.value)}
                    placeholder="No one assigned"
                    className="input"
                  />
                </div>

                {/* Inspection domain */}
                <div className="form-group">
                  <label className="label" htmlFor="insp-domain">
                    Inspection Type
                  </label>
                  <select
                    id="insp-domain"
                    value={inspectionDomain}
                    onChange={(e) => handleInspectionDomainChange(e.target.value)}
                    className="input select"
                  >
                    <option value="" disabled>
                      Select inspection type
                    </option>
                    <option value="ELECTRIC_NETWORK">Electric Network</option>
                    <option value="EMISSIONS">Emissions</option>
                    <option value="THERMAL_PIPELINE">Thermal Pipeline</option>
                    <option value="OIL_SPILL">Oil Spill</option>
                    <option value="PPE">PPE</option>
                    <option value="SOLAR_PANEL">Solar Panels</option>
                  </select>
                </div>

                {/* Image modality */}
                <div className="form-group">
                  <label className="label" htmlFor="image-modality">
                    Image Modality
                  </label>
                  <select
                    id="image-modality"
                    value={imageModality}
                    onChange={(e) => setImageModality(e.target.value)}
                    className="input select"
                    disabled={(modalityOptionsByDomain[inspectionDomain] || ["RGB"]).length === 1}
                  >
                    {(modalityOptionsByDomain[inspectionDomain] || ["RGB"]).map((option) => (
                      <option key={option} value={option}>
                        {imageModalityLabels[option] || option}
                      </option>
                    ))}
                  </select>
                </div>
                {/* KMZ */}
                <div className="form-group checkbox-row">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={hasKmz}
                      onChange={() => setHasKmz(!hasKmz)}
                    />
                    <span>Do you have a KMZ for this Inspection</span>
                  </label>
                </div>

                {hasKmz && (
                  <div className="form-group">
                    <label className="label" htmlFor="kmz-upload">
                      KMZ File
                    </label>
                    <div className="upload-row">
                      <label htmlFor="kmz-upload" className="btn-upload">
                        📎 Upload KMZ File
                      </label>
                      <input
                        id="kmz-upload"
                        type="file"
                        accept=".kmz"
                        onChange={(e) => setKmzFile(e.target.files[0])}
                        className="hidden-input"
                      />
                      {kmzFile && (
                        <p className="file-name">Selected: {kmzFile.name}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <footer className="sheet-footer">
                <button className="btn btn-ghost" onClick={() => setShowModal(false)} disabled={isCreating}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={confirmNewInspection} disabled={isCreating}>
                  {isCreating ? "Creating…" : "Create"}
                </button>
              </footer>
            </aside>
          </div>
        )}
      </div>
      <LoadingOverlay show={isCreating} text="Creating inspection…" />
    </div>
  );
};

export default ProjectDetails;
